<script setup lang="ts">
import { useMutation, useQuery } from '@vue/apollo-composable';
import { computed, ref } from 'vue';

import { useAuthStore } from '@/entities/auth';
import { paymentQueryNames } from '@/entities/payment';
import { getGraphQLErrorCode, useConfirm, useToast } from '@/shared/lib';
import { AppButton } from '@/shared/ui';
import {
  ForceCloseOrderFiscalOperationDocument,
  GetOrderFiscalOperationDocument,
  OrderFiscalBankPaymentStatus,
  type OrderFiscalOperationFieldsFragment,
  OrderFiscalOperationType,
} from '@shared/api/graphql/gql/graphql';

const props = defineProps<{
  operationId: string;
  operation?: OrderFiscalOperationFieldsFragment;
}>();
const emit = defineEmits<{ closed: [] }>();
const authStore = useAuthStore();
const toast = useToast();
const { prompt } = useConfirm();
const closing = ref(false);
const canForceClose = computed(() => authStore.hasPermission('fiscalization.force_close'));
const { result } = useQuery(
  GetOrderFiscalOperationDocument,
  () => ({ operationId: props.operationId }),
  () => ({ enabled: canForceClose.value && !props.operation, fetchPolicy: 'network-only' }),
);
const currentOperation = computed(() => props.operation ?? result.value?.orderFiscalOperation);
const { mutate: forceClose } = useMutation(ForceCloseOrderFiscalOperationDocument, {
  refetchQueries: [...paymentQueryNames],
  context: { suppressGlobalErrorToast: true },
});

function forceCloseMessage(operation: OrderFiscalOperationFieldsFragment): string {
  const bankStatus = operation.bank_payment_status;
  if (
    bankStatus === OrderFiscalBankPaymentStatus.Captured ||
    bankStatus === OrderFiscalBankPaymentStatus.Refunded ||
    bankStatus === OrderFiscalBankPaymentStatus.RefundedUnverified
  ) {
    return operation.operation_type === OrderFiscalOperationType.Refund
      ? 'Деньги, возвращённые банком, будут учтены; чек не оформлен.'
      : 'Деньги, списанные банком, будут учтены; чек не оформлен.';
  }

  return 'Закрывается только фискальная операция, остальные данные в учёте не изменятся.';
}

async function closeForcefully(): Promise<void> {
  const operation = currentOperation.value;
  if (!canForceClose.value || !operation || closing.value) return;
  const reason = await prompt({
    title: 'Закрыть фискальную операцию принудительно',
    message: `Это действие необратимо. ${forceCloseMessage(operation)}`,
    confirmText: 'Закрыть принудительно',
    inputLabel: 'Причина аварийного закрытия',
    inputPlaceholder: 'Опишите, почему операция больше не должна блокировать кассу',
  });

  if (!reason?.trim()) return;

  closing.value = true;
  try {
    const response = await forceClose({
      input: {
        operationId: operation.id,
        reason: reason.trim(),
      },
    });
    if (!response?.data?.forceCloseOrderFiscalOperation) {
      toast.error('Не удалось закрыть фискальную операцию');
      return;
    }

    emit('closed');
    toast.show('Фискальная операция закрыта принудительно');
  } catch (mutationError) {
    const message =
      getGraphQLErrorCode(mutationError) === 'order_fiscalization_force_close_invalid_state'
        ? 'Операция уже закрыта или больше не может быть закрыта принудительно'
        : 'Не удалось закрыть фискальную операцию';
    toast.error(message);
  } finally {
    closing.value = false;
  }
}
</script>

<template>
  <AppButton
    v-if="canForceClose"
    size="sm"
    outline
    :disabled="closing || !currentOperation"
    :loading="closing"
    @click="closeForcefully"
  >
    Закрыть принудительно
  </AppButton>
</template>
